import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './AnalyticsChart.css';

function AnalyticsChart({ accountData, compareMode, onReconnect }) {
  // Check if error is authentication-related
  const isAuthError = accountData.error && /invalid_grant|token|unauthorized|expired/i.test(accountData.error);
  const chartData = useMemo(() => {
    if (accountData.error) {
      return [];
    }

    const { data, comparisonData, trend } = accountData;

    // Prepare data for Recharts
    return data.map((item, index) => {
      const date = new Date(item.date.slice(0, 4), item.date.slice(4, 6) - 1, item.date.slice(6, 8));
      const formattedDate = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const chartItem = {
        date: formattedDate,
        currentPeriod: item.users
      };

      // Add comparison data if available
      if (compareMode && comparisonData && comparisonData[index]) {
        chartItem.previousPeriod = comparisonData[index].users;
      }

      // Add trend line data if available
      if (trend && trend.trendLine && trend.trendLine[index]) {
        chartItem.trendValue = trend.trendLine[index].trendValue;
      }

      return chartItem;
    });
  }, [accountData, compareMode]);

  if (accountData.error) {
    return (
      <div className="chart-card">
        <div className="chart-error">
          <h3>{accountData.propertyName}</h3>
          <p className="error-message">Error: {accountData.error}</p>
          {accountData.email && (
            <p className="error-email">{accountData.email}</p>
          )}
          {isAuthError && onReconnect && (
            <button
              className="btn btn-primary btn-small"
              onClick={() => onReconnect(accountData.email)}
              title="Reconnect this account with Google Analytics"
            >
              Reconnect Account
            </button>
          )}
        </div>
      </div>
    );
  }

  const { totalUsers, period, propertyName, websiteUrl, websiteName, email } = accountData;

  const analyticsUrl = `https://analytics.google.com/analytics/web/?authuser=${email}#/a${accountData.accountId}p${accountData.propertyId}/reports/intelligenthome`;

  // Debug logging
  console.log('Account data:', {
    propertyName,
    websiteName,
    websiteUrl,
    hasWebsiteName: !!websiteName
  });

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div>
          <h3 title={email ? `Account email: ${email}` : propertyName}>
            <a href={analyticsUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              📊 {propertyName}
            </a>
          </h3>
          {(websiteName || websiteUrl) && (
            <p className="chart-subtitle">
              <a
                href={websiteUrl.startsWith('http') ? websiteUrl : `https://${websiteUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="website-link"
                title={`Open ${websiteUrl} in new tab`}
              >
                🔗 {websiteName || websiteUrl}
              </a>
            </p>
          )}
        </div>
      </div>
      <div className="chart-stats">
        <div className="stat-item">
          <span className="stat-label">Total Users</span>
          <span className="stat-value">{totalUsers.toLocaleString()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Period</span>
          <span className="stat-value-small">
            {period.startDate} to {period.endDate}
          </span>
        </div>
        {accountData.trend && (
          <div className="stat-item">
            <span className="stat-label">Trend</span>
            <div className="trend-details">
              <span className={`stat-value ${accountData.trend.slope >= 0 ? 'trend-up' : 'trend-down'}`}>
                {accountData.trend.slope >= 0 ? '↑' : '↓'} {Math.abs(accountData.trend.percentChange).toFixed(1)}%
              </span>
              <span className="stat-value-small trend-users">
                {(() => {
                  if (accountData.trend && typeof accountData.trend.netChange !== 'undefined') {
                    const change = Math.round(accountData.trend.netChange);
                    return `${change > 0 ? '+' : ''}${change.toLocaleString()} users`;
                  }
                  // Fallback to raw calculation if trend data missing (shouldn't happen given parent check)
                  if (!accountData.data || accountData.data.length === 0) return '0 users';
                  const sorted = [...accountData.data].sort((a, b) => a.date.localeCompare(b.date));
                  const first = sorted[0].users;
                  const last = sorted[sorted.length - 1].users;
                  const userChange = last - first;
                  return `${userChange > 0 ? '+' : ''}${userChange.toLocaleString()} users`;
                })()}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={350}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8eaed" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: '#5f6368' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#5f6368' }}
              tickLine={false}
              domain={[0, 'auto']}
              allowDataOverflow={false}
              label={{ value: 'Users', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#5f6368' } }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e8eaed',
                borderRadius: '4px',
                fontSize: '12px'
              }}
              formatter={(value) => {
                if (typeof value === 'number') {
                  return Math.round(value).toLocaleString();
                }
                return value;
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={36}
              iconType="line"
            />
            <Line
              type="monotone"
              dataKey="currentPeriod"
              stroke="#4285f4"
              strokeWidth={2}
              dot={{ fill: '#4285f4', r: 3 }}
              activeDot={{ r: 5 }}
              name="Current Period"
            />
            {compareMode && (
              <Line
                type="monotone"
                dataKey="previousPeriod"
                stroke="#ea4335"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#ea4335', r: 3 }}
                activeDot={{ r: 5 }}
                name="Previous Period"
              />
            )}
            {accountData.trend && (
              <Line
                type="monotone"
                dataKey="trendValue"
                stroke={accountData.trend.slope >= 0 ? "#34a853" : "#ea4335"}
                strokeWidth={2}
                strokeDasharray="3 3"
                dot={false}
                name="Trend"
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default AnalyticsChart;
